/**
 * Vehicle API utility for fetching vehicle makes, models, and other data
 * Uses the NHTSA Vehicle API (free, no authentication required)
 */

// Base URL for the NHTSA API
const BASE_URL = 'https://vpic.nhtsa.dot.gov/api/vehicles';

// List of common car manufacturers to filter by
const CAR_MANUFACTURERS = [
  'Acura', 'Alfa Romeo', 'Aston Martin', 'Audi', 'Bentley', 'BMW', 'Bugatti',
  'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 'Citroen', 'Dodge', 'Ferrari',
  'Fiat', 'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai', 'Infiniti', 'Jaguar',
  'Jeep', 'Kia', 'Lamborghini', 'Land Rover', 'Lexus', 'Lincoln', 'Lotus',
  'Maserati', 'Mazda', 'McLaren', 'Mercedes-Benz', 'Mini', 'Mitsubishi',
  'Nissan', 'Pagani', 'Peugeot', 'Porsche', 'Ram', 'Renault', 'Rolls-Royce',
  'Saab', 'Subaru', 'Suzuki', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo'
];

/**
 * Fetches all vehicle makes (manufacturers)
 * @param {boolean} carsOnly - Whether to filter for car manufacturers only
 * @returns {Promise<Array>} Array of vehicle makes
 */
export const getAllMakes = async (carsOnly = true) => {
  try {
    const response = await fetch(`${BASE_URL}/GetAllMakes?format=json`);
    const data = await response.json();
    
    if (data.Results && Array.isArray(data.Results)) {
      let makes = data.Results;
      
      // Filter for car manufacturers only if requested
      if (carsOnly) {
        makes = makes.filter(make => 
          CAR_MANUFACTURERS.some(carMake => 
            make.Make_Name.toLowerCase().includes(carMake.toLowerCase())
          )
        );
      }
      
      // Sort makes alphabetically
      return makes.sort((a, b) => a.Make_Name.localeCompare(b.Make_Name));
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching vehicle makes:', error);
    return [];
  }
};

/**
 * Fetches all models for a specific make
 * @param {string} make - The vehicle make/manufacturer
 * @returns {Promise<Array>} Array of vehicle models for the specified make
 */
export const getModelsByMake = async (make) => {
  if (!make) return [];
  
  try {
    // URL encode the make name to handle special characters
    const encodedMake = encodeURIComponent(make);
    const response = await fetch(`${BASE_URL}/GetModelsForMake/${encodedMake}?format=json`);
    const data = await response.json();
    
    if (data.Results && Array.isArray(data.Results)) {
      // Sort models alphabetically and remove duplicates
      const uniqueModels = Array.from(
        new Set(data.Results.map(model => model.Model_Name))
      ).map(name => {
        const model = data.Results.find(m => m.Model_Name === name);
        return {
          Model_ID: model.Model_ID,
          Model_Name: name
        };
      });
      
      return uniqueModels.sort((a, b) => a.Model_Name.localeCompare(b.Model_Name));
    }
    
    return [];
  } catch (error) {
    console.error(`Error fetching models for make ${make}:`, error);
    return [];
  }
};

/**
 * Fetches vehicle types (e.g., Car, Truck, Motorcycle)
 * @returns {Promise<Array>} Array of vehicle types
 */
export const getVehicleTypes = async () => {
  try {
    const response = await fetch(`${BASE_URL}/GetVehicleTypesForMake/honda?format=json`);
    const data = await response.json();
    
    if (data.Results && Array.isArray(data.Results)) {
      // Extract unique vehicle types
      const uniqueTypes = [...new Set(data.Results.map(item => item.VehicleTypeName))];
      return uniqueTypes.sort();
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching vehicle types:', error);
    return [];
  }
};

/**
 * Gets a range of years for vehicle selection
 * @param {number} startYear - The starting year (defaults to 1995)
 * @returns {Array<number>} Array of years from startYear to current year
 */
export const getYearRange = (startYear = 1995) => {
  const currentYear = new Date().getFullYear() + 1; // Include next year's models
  const years = [];
  
  for (let year = currentYear; year >= startYear; year--) {
    years.push(year);
  }
  
  return years;
};

/**
 * Common vehicle colors
 * @returns {Array<string>} Array of common vehicle colors
 */
export const getCommonColors = () => {
  return [
    'Black',
    'White',
    'Silver',
    'Gray',
    'Red',
    'Blue',
    'Brown',
    'Green',
    'Beige',
    'Gold',
    'Orange',
    'Purple',
    'Yellow',
    'Other'
  ];
};

export default {
  getAllMakes,
  getModelsByMake,
  getVehicleTypes,
  getYearRange,
  getCommonColors
}; 